import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

/**
 * ADR-015 Fase 3: proposta de resumo pendente para uma oferta.
 * Vive em deal_context tipo='resumo_proposal' (criada pelo trigger
 * trg_briefing_resumo_proposal quando resumo_source IN ('human','merged')).
 * Usa RPC update_proposal_status para mudar metadata.proposal_status
 * atomicamente (evita race do read-then-update).
 */
export const useResumoProposal = (ofertaId) => {
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProposal = useCallback(async () => {
    if (!ofertaId) {
      setProposal(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from('deal_context')
      .select('id, texto, autor_id, metadata, created_at')
      .eq('oferta_id', ofertaId)
      .eq('tipo', 'resumo_proposal')
      .filter('metadata->>proposal_status', 'eq', 'pending')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!error) setProposal(data || null);
    setLoading(false);
  }, [ofertaId]);

  useEffect(() => { fetchProposal(); }, [fetchProposal]);

  /**
   * Aceita a proposta (ou versao editada): atualiza ofertas.resumo_estagio,
   * marca resumo_source='merged' e seta proposal_status accepted|edited.
   * acceptedText opcional — default = texto original da proposta.
   */
  const acceptProposal = useCallback(async ({ proposalId, acceptedText, acceptedBy, edited = false }) => {
    const text = (acceptedText || '').trim();
    if (!text) return { error: { message: 'texto vazio' } };

    const { error: e1 } = await supabase
      .from('ofertas')
      .update({
        resumo_estagio: text,
        resumo_source: 'merged',
        resumo_estagio_fonte: edited ? 'ai_editado' : 'ai_briefing',
        resumo_estagio_updated_at: new Date().toISOString(),
        resumo_estagio_updated_by: acceptedBy || 'usuario',
        updated_at: new Date().toISOString(),
      })
      .eq('id', ofertaId);
    if (e1) return { error: e1 };

    const { error: e2 } = await supabase.rpc('update_proposal_status', {
      p_id: proposalId,
      p_status: edited ? 'edited' : 'accepted',
      p_by: acceptedBy || null,
    });
    if (e2) return { error: e2 };

    setProposal(null);
    return { error: null };
  }, [ofertaId]);

  const rejectProposal = useCallback(async ({ proposalId, rejectedBy }) => {
    const { error } = await supabase.rpc('update_proposal_status', {
      p_id: proposalId,
      p_status: 'rejected',
      p_by: rejectedBy || null,
    });
    if (!error) setProposal(null);
    return { error };
  }, []);

  return {
    proposal,
    loading,
    acceptProposal,
    rejectProposal,
    refetch: fetchProposal,
  };
};

export default useResumoProposal;
